import React from 'react';
import { Link } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { SectionNav } from '../components/SectionNav';
import { shippingReturnsSections, type TextBlock } from '../data/legalContent';

/**
 * /shipping-returns — the long-form text template.
 * Reusable for Privacy, Terms and any other policy page: swap the
 * sections array, keep the layout.
 */
export function ShippingReturns() {
  return <div className="w-full bg-paper" data-id="element-803" data-mp="3u2d2c8nvli8f">
      <PageHero eyebrow="Help" title="Returns & Refunds Policy" intro="Your rights, and our procedures for returns, exchanges, repairs and refunds — in line with the Consumer Protection Act." data-id="element-804" data-mp="2vio1945uh9e" />

      <SectionNav items={shippingReturnsSections.map(s => ({
      label: s.title,
      to: `#${s.id}`
    }))} data-id="element-805" data-mp="3pabjg3w7ct9f" />

      <div className="mx-auto max-w-[1400px] px-6 py-20 md:py-24" data-id="element-806" data-mp="gt70pto2qi3d">
        <div className="max-w-[720px]" data-id="element-807" data-mp="lmtfpshpmy2c">
          <p className="type-eyebrow text-[9px] text-coffee/45" data-id="element-808" data-mp="lnusc1edjsm6">
            Last updated September 2026
          </p>

          {/* Body */}
          <div className="mt-12" data-id="element-809" data-mp="h6hrkcnxswyt">
            {shippingReturnsSections.map((s, i) => <section key={s.id} id={s.id} className={`scroll-mt-40 ${i > 0 ? 'mt-20 border-t border-oat pt-16' : ''}`} data-id="element-810" data-mp="vv4hf8b6x74a" data-mpi={i}>
                <h2 className="type-display text-4xl text-coffee md:text-5xl" data-id="element-811" data-mp="14tzloeuoc74y">
                  {s.title}
                </h2>
                <div className="mt-8" data-id="element-812" data-mp="10tflt3ipupf4">
                  {s.blocks.map((b, index) => <Block key={index} block={b} data-id="element-813" data-mp="1esp5id64s1nu" data-mpi={index} />)}
                </div>
              </section>)}

            {/* Close */}
            <div className="mt-20 rounded-4xl bg-paper-warm p-9 md:p-12" data-id="element-814" data-mp="3gkbobrfvcq98">
              <h2 className="type-display text-3xl text-coffee" data-id="element-815" data-mp="3blqnpqwpg4k9">
                Still need a hand?
              </h2>
              <p className="type-body mt-3 max-w-md text-sm text-ink/65" data-id="element-816" data-mp="1fj5ezu72kgpb">
                Our FAQs cover lids, sleeves, sizing and care. Anything else,
                send us a message and we will come back to you.
              </p>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-817" data-mp="1oiinokndd18z">
                <Link to="/contact" className="type-cta rounded-full bg-coffee px-8 py-4 text-[11px] text-paper transition-colors hover:bg-pennant" data-id="element-818" data-mp="1ti50wu376hhs">
                  Contact us
                </Link>
                <Link to="/faqs" className="type-cta rounded-full border border-coffee/25 px-8 py-4 text-[11px] text-coffee transition-colors hover:bg-coffee hover:text-paper" data-id="element-819" data-mp="23eqaztu2qish">
                  Read the FAQs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function Block({
  block
}: {
  block: TextBlock;
}) {
  switch (block.type) {
    case 'p':
      return <p className="type-body mt-5 text-base leading-relaxed text-ink/70 first:mt-0" data-id="element-820" data-mp="2wbsfonk1mq7d">
          {block.text}
        </p>;
    case 'h3':
      return <h3 className="type-eyebrow mt-12 text-[10px] text-pennant" data-id="element-821" data-mp="2mc9y2drlvgih">
          {block.text}
        </h3>;
    case 'list':
      return <ul className="mt-6 space-y-4" data-id="element-822" data-mp="2icsslkc6ijdb">
          {block.items.map(item => <li key={item} className="flex gap-4" data-id="element-823" data-mp="3f05s1apsiubk">
              <span aria-hidden="true" className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-pennant" data-id="element-824" data-mp="3a2e0wiwyzc8f" />
              <span className="type-body text-base leading-relaxed text-ink/70" data-id="element-825" data-mp="35actpbensbp9">
                {item}
              </span>
            </li>)}
        </ul>;
    case 'note':
      return <p className="type-body mt-8 border-l-2 border-pennant bg-paper-warm px-7 py-6 text-base leading-relaxed text-ink/70" data-id="element-826" data-mp="1ndrx79f8uak">
          {block.text}
        </p>;
    case 'table':
      return <div className="mt-8 overflow-hidden rounded-3xl border border-oat" data-id="element-827" data-mp="3dhnos78mk5qx">
          <table className="w-full border-collapse text-left" data-id="element-828" data-mp="3ijd07nzrelfj">
            <thead data-id="element-829" data-mp="34ik6h5ha7udh">
              <tr className="bg-paper-warm" data-id="element-830" data-mp="34j2cgttqxmyl">
                {block.head.map(h => <th key={h} scope="col" className="type-eyebrow px-6 py-4 text-[9px] text-coffee/55" data-id="element-831" data-mp="38jw0dn76v1rh">
                    {h}
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-832" data-mp="3nhei45lg8epf">
              {block.rows.map(row => <tr key={row[0]} className="border-t border-oat" data-id="element-833" data-mp="3nh65met8nber">
                  {row.map((cell, i) => <td key={i} className={`px-6 py-4 text-sm ${i === 0 ? 'type-ui text-coffee' : 'type-body text-ink/65'}`} data-id="element-834" data-mp="3rgx4l5sx4xud" data-mpi={i}>
                      {cell}
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>;
    default:
      return null;
  }
}